import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Warm in-app announcement banner. Shows one item at a time, above the
 * navbar, for logged-in users only. Dismissal is persisted server-side so
 * it stays hidden across devices.
 */
export default function AnnouncementBanner() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!user) { setItems([]); return; }
    let alive = true;
    api.get("/announcements/active")
      .then(({ data }) => { if (alive) setItems(data.results || []); })
      .catch((e) => console.warn("[banner] fetch failed", e));
    return () => { alive = false; };
  }, [user]);

  if (!user || items.length === 0) return null;
  const item = items[0];

  const dismiss = async () => {
    setItems((prev) => prev.filter((i) => i.id !== item.id));
    try { await api.post(`/announcements/${item.id}/dismiss`); }
    catch (e) { console.warn("[banner] dismiss failed", e); }
  };

  const firstName = (user.name || "there").split(" ")[0];

  return (
    <div
      role="status"
      data-testid="announcement-banner"
      className="relative bg-gradient-to-r from-ochre-500 via-ochre-600 to-ochre-500 text-white shadow-sm"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-3 flex items-start sm:items-center gap-3 sm:gap-4">
        <span className="hidden sm:inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/20 backdrop-blur shrink-0">
          <Sparkles className="w-4 h-4" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm sm:text-[15px] leading-snug">
            <span className="font-semibold">Hey {firstName} 👋 </span>
            <span className="text-white/95">{item.message}</span>
          </div>
        </div>
        {item.cta_link && (
          <Link
            to={item.cta_link}
            data-testid="announcement-cta"
            onClick={dismiss}
            className="inline-flex items-center gap-1.5 rounded-full bg-white text-sage-900 hover:bg-sage-50 px-3.5 py-1.5 text-xs sm:text-sm font-semibold shadow whitespace-nowrap transition-colors duration-200"
          >
            <span className="hidden sm:inline">{item.cta_label}</span>
            <span className="sm:hidden">Fix now</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        )}
        <button
          type="button"
          onClick={dismiss}
          data-testid="announcement-dismiss"
          aria-label="Dismiss announcement"
          className="p-1.5 rounded-full text-white/80 hover:text-white hover:bg-white/15 transition-colors duration-200"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
